import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { Ruleset, Discord } from './RulesetApi';
import { useTranslation } from 'react-i18next';
import { DataGrid, GridActionsCellItem, GridRowId } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Chip, Button, Box } from '@mui/material';
import GridItem from 'components/Grid/GridItem';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import { useTheme } from '@mui/material/styles';

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  chip: {
    marginRight: theme.spacing(1),
  },
  published: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  unpublished: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  deleteButton: {
    marginTop: theme.spacing(2),
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    justifyContent: 'flex-start',
  },
}));

interface RulesetsTableProps {
  rulesets: Ruleset[];
  discords?: Discord[];
  onEdit: (ruleset: Ruleset) => void;
  onDelete: (id: string) => void;
}

const RulesetsTable: React.FC<RulesetsTableProps> = ({
  rulesets,
  discords = [],
  onEdit,
  onDelete,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rulesetToDelete, setRulesetToDelete] = useState<Ruleset | null>(null);

  const handleDeleteClick = (ruleset: Ruleset) => {
    setRulesetToDelete(ruleset);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (rulesetToDelete && rulesetToDelete.id) {
      onDelete(rulesetToDelete.id);
    }
    setDeleteDialogOpen(false);
    setRulesetToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRulesetToDelete(null);
  };

  const handleDeleteSelected = () => {
    selectedRows.forEach((id) => {
      onDelete(id.toString());
    });
    setSelectedRows([]);
  };

  // Helper function to get Discord team name by ID
  const getDiscordTeamName = (discordId: string) => {
    const discord = discords.find((d) => d.id === discordId);
    return discord ? discord.name : t('rulesets.table.noTeam');
  };

  return (
    <GridItem xs={12} sm={12} md={12}>
      <DataGrid
        rows={rulesets}
        columns={[
          {
            field: 'name',
            headerName: t('rulesets.table.name'),
            flex: 1,
            renderCell: (params) => (
              <Link
                to={`/ruleset/${params.row.id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                {params.value}
              </Link>
            ),
          },
          {
            field: 'description',
            headerName: t('rulesets.table.description'),
            flex: 2,
            renderCell: (params) => (
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '400px',
                }}
              >
                {params.value}
              </div>
            ),
          },
          {
            field: 'discord_id',
            headerName: t('rulesets.table.team'),
            flex: 1,
            valueGetter: (params) => {
              // If team_name is available, use it (for backward compatibility)
              if (params.row.team_name) {
                return params.row.team_name;
              }
              // Otherwise use the discord_id to find the team name
              return getDiscordTeamName(params.value as string);
            },
          },
          {
            field: 'published',
            headerName: t('rulesets.table.status'),
            flex: 1,
            renderCell: (params) => (
              <Chip
                icon={params.value ? <CheckCircleIcon /> : <CancelIcon />}
                label={
                  params.value ? t('rulesets.status.published') : t('rulesets.status.unpublished')
                }
                className={`${
                  params.value ? classes.published : classes.unpublished
                } ${classes.chip}`}
                size="small"
              />
            ),
          },
          {
            field: 'actions',
            type: 'actions',
            headerName: t('common.dataGrid.rowActions'),
            flex: 1,
            getActions: (params) => [
              <GridActionsCellItem
                key="edit"
                icon={<EditIcon />}
                label={t('common.dataGrid.edit')}
                onClick={() => onEdit(params.row)}
                color={isDarkMode ? 'inherit' : 'primary'}
              />,
              <GridActionsCellItem
                key="delete"
                icon={<DeleteIcon />}
                label={t('common.dataGrid.delete')}
                onClick={() => handleDeleteClick(params.row)}
                color="error"
              />,
            ],
          },
        ]}
        autoHeight
        checkboxSelection
        onRowSelectionModelChange={(newSelection) => {
          setSelectedRows(newSelection);
        }}
        disableRowSelectionOnClick
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[5, 10, 25]}
        localeText={{
          noRowsLabel: t('common.dataGrid.noRows'),
          noResultsOverlayLabel: t('common.dataGrid.noResults'),
          toolbarColumns: t('common.dataGrid.toolbar.columns'),
          toolbarExport: t('common.dataGrid.toolbar.export'),
          toolbarFilters: t('common.dataGrid.toolbar.search'),
          columnMenuLabel: t('common.dataGrid.columnMenu.columns'),
          columnMenuShowColumns: t('common.dataGrid.columnMenu.columns'),
          columnMenuFilter: t('common.dataGrid.columnMenu.filter'),
          columnMenuHideColumn: t('common.dataGrid.columnMenu.hideColumn'),
          columnMenuSortAsc: `${t('common.dataGrid.columnMenu.sort')} (A-Z)`,
          columnMenuSortDesc: `${t('common.dataGrid.columnMenu.sort')} (Z-A)`,
          footerRowSelected: (count) =>
            count !== 1
              ? `${count} ${t('rulesets.table.deleteSelected', { count })}`
              : `${count} ${t('rulesets.table.deleteSelected', { count })}`,
          MuiTablePagination: {
            labelRowsPerPage: t('common.dataGrid.pagination.rowsPerPage'),
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${t('common.dataGrid.pagination.of')} ${count}`,
          },
        }}
      />

      <Box className={classes.buttonContainer}>
        {selectedRows.length > 0 && (
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteSelected}
            className={classes.deleteButton}
            size="medium"
          >
            {t('rulesets.table.deleteSelected', { count: selectedRows.length })}
          </Button>
        )}
      </Box>

      <ConfirmDialog
        open={deleteDialogOpen}
        title={t('rulesets.dialog.deleteTitle')}
        content={t('rulesets.dialog.deleteContent', {
          name: rulesetToDelete?.name || '',
        })}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </GridItem>
  );
};

export default RulesetsTable;
